import { createFileRoute, Link } from '@tanstack/react-router';
import { statementDisclaimer } from '@namibiago/shared/copy';
import { useState } from 'react';
import { useAuth } from '@/lib/auth/authContext';
import { useProperty } from '@/lib/property/propertyContextDef';
import { OwnerOnly } from '@/lib/auth/OwnerOnly';
import { monthLabel, useDownloadStatementPdf, useStatement } from '@/lib/data/statements';
import { formatEurCents, formatNad } from '@/lib/money/format';
import { BalanceByMethodSummary, StatementBreakdownTable } from '@/lib/statements/StatementBreakdown';
import { DownloadIcon } from '@/icons';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/statements/$period/')({
  component: StatementDetailPage,
});

function StatementDetailPage() {
  return (
    <OwnerOnly>
      <StatementDetail />
    </OwnerOnly>
  );
}

function StatementDetail() {
  const { period } = Route.useParams();
  const { supabase } = useAuth();
  const { businessId, memberships } = useProperty();
  const { data: statement, isLoading, error } = useStatement(supabase, businessId, period);
  const downloadPdf = useDownloadStatementPdf(supabase);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const businessName = memberships.find((m) => m.businessId === businessId)?.businessName ?? '';

  function handleDownload() {
    if (!statement?.pdf_path) return;
    setDownloadError(null);
    downloadPdf.mutate(
      { pdfPath: statement.pdf_path, filename: `namibiago-statement-${statement.period}.pdf` },
      { onError: (err) => setDownloadError(err instanceof Error ? err.message : 'Could not download the PDF.') },
    );
  }

  if (isLoading) return <PageLoading />;
  if (error) return <p className="text-body text-danger">Couldn't load this statement: {(error as Error).message}</p>;
  if (!statement) return <p className="text-body text-ink-2">No statement found for {monthLabel(period)}.</p>;

  return (
    <section className="flex flex-col gap-lg">
      <div className="flex flex-wrap items-center justify-between gap-md">
        <div>
          <Link to="/statements" className="text-caption text-ink-2 underline">
            ← All statements
          </Link>
          {/* `period` (the URL param) rather than `statement.period` — always a valid
              string regardless of the query's loading/error/shape state, so the
              heading never depends on `statement` actually being a real row (a
              test-double fake client that hands back a non-null-but-shapeless
              `data` here must not crash monthLabel()'s `.split('-')`). */}
          <h1 className="mt-xs text-screen-title font-extrabold tracking-tight text-ink">{monthLabel(period)}</h1>
          <p className="text-body text-ink-2">{businessName}</p>
        </div>
        <div className="flex items-center gap-sm">
          <Link
            to="/statements/$period/print"
            params={{ period }}
            className="rounded-button border border-border bg-surface px-md py-xs text-body text-ink"
          >
            Print
          </Link>
          <button
            type="button"
            onClick={handleDownload}
            disabled={!statement.pdf_path || downloadPdf.isPending}
            className="inline-flex items-center gap-xs rounded-button bg-canyon px-md py-xs text-body font-semibold text-white disabled:opacity-50"
          >
            <DownloadIcon className="h-4 w-4" aria-hidden="true" />
            {downloadPdf.isPending ? 'Downloading…' : 'Download PDF'}
          </button>
        </div>
      </div>

      {downloadError ? <p className="text-body text-danger">{downloadError}</p> : null}
      {!statement.pdf_path ? (
        <p className="text-body text-ink-2">The PDF for this period hasn't generated yet — check back after the 1st.</p>
      ) : null}

      <dl className="grid grid-cols-1 gap-y-sm rounded-card border border-border bg-surface p-lg shadow-card sm:grid-cols-2">
        <dt className="text-ink-2">Bookings paid in-app this month</dt>
        <dd className="font-semibold text-ink">{statement.bookings_count}</dd>

        <dt className="text-ink-2">Fees guests paid to NamibiaGo</dt>
        <dd className="font-semibold text-ink">
          {formatNad(statement.fees_paid_nad)}
          {statement.fees_paid_eur_cents != null ? ` (${formatEurCents(statement.fees_paid_eur_cents)})` : ''}
        </dd>

        {Number(statement.guest_payments_nad ?? 0) > 0 ? (
          <>
            <dt className="text-ink-2">Full payments guests made in-app</dt>
            <dd className="text-ink">{formatNad(statement.guest_payments_nad)}</dd>
          </>
        ) : null}

        <dt className="text-ink-2">Refunded to guests</dt>
        <dd className="text-ink">{formatNad(statement.fees_refunded_nad)}</dd>

        <dt className="text-ink-2">Balances you recorded</dt>
        <dd className="font-semibold text-ink">{formatNad(statement.balances_recorded_nad)}</dd>
      </dl>

      <BalanceByMethodSummary balanceByMethod={statement.balance_by_method ?? {}} />
      <StatementBreakdownTable lines={statement.line_items ?? []} />

      {/* NEW-4: one shared source with the PDF; the balance-only sentence only
          when the month has no full in-app payments (decision 13a wording). */}
      <p className="text-caption text-ink-3" data-testid="statement-disclaimer">
        {statementDisclaimer(statement.guest_payments_nad).join(' ')} Generated{' '}
        {new Date(statement.generated_at).toLocaleDateString()}.
      </p>
    </section>
  );
}
